import { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from '../Checkbox';
import { useClickOutside } from '../../hooks/useClickOutside';
import { TuneIcon } from '../../icons/TuneIcon';
import { Panel, PanelOption, Trigger, TriggerLabel, Wrapper } from './FilterMenu.styles';
import type { FilterMenuProps } from './FilterMenu.types';

export function FilterMenu({
  options,
  selectedValues,
  onChange,
  label,
  align = 'right',
  'aria-label': ariaLabel,
}: FilterMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, handleClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleToggle = (value: string, checked: boolean) => {
    onChange(
      checked ? [...selectedValues, value] : selectedValues.filter((selected) => selected !== value)
    );
  };

  return (
    <Wrapper ref={wrapperRef} data-open={isOpen}>
      <Trigger
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        data-active={selectedValues.length > 0}
        aria-label={ariaLabel ?? label}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <TuneIcon size={24} />
        <TriggerLabel>{label}</TriggerLabel>
      </Trigger>
      {isOpen && (
        <Panel data-align={align} aria-label={ariaLabel ?? label}>
          {options.map((option) => (
            <PanelOption key={option.value}>
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onChange={(checked) => handleToggle(option.value, checked)}
                label={option.label}
              />
            </PanelOption>
          ))}
        </Panel>
      )}
    </Wrapper>
  );
}
